import React from 'react';
import logo from '../images/logoaccess.png';
import { getText, getIdiomaLocale } from '../util/translations';
import { getParticipantAuth, getInternalAuth } from '../util/authSession';

/**
 * ErrorBoundary — evita a "tela branca" (white screen of death).
 *
 * Sem este boundary, qualquer exceção lançada durante a renderização de um
 * componente filho desmonta toda a árvore React e a página fica em branco,
 * sem nenhuma pista para o usuário e sem log (o projeto não tem captura de
 * erro de cliente, ex.: Sentry). Aqui interceptamos a falha, mostramos um
 * fallback amigável com ação de recarregar e deixamos o stack no console
 * para diagnóstico.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, ocorridoEm: null };
    }

    static getDerivedStateFromError() {
        // captura o instante da ocorrência (mostrado na tela + enviado no log),
        // para cruzar um print do usuário com a entrada exata no GCloud
        return { hasError: true, ocorridoEm: new Date() };
    }

    componentDidCatch(error, errorInfo) {
        // eslint-disable-next-line no-console
        console.error(
            '[ErrorBoundary] erro de renderização capturado:',
            error,
            errorInfo && errorInfo.componentStack
        );
        this.reportar(error, errorInfo);
    }

    componentDidUpdate(prevProps) {
        // Ao navegar para outra rota depois de um erro, limpa o estado para a
        // nova tela renderizar normalmente (em vez de ficar presa no fallback).
        if (this.state.hasError && prevProps.pathname !== this.props.pathname) {
            this.setState({ hasError: false });
        }
    }

    // Best-effort: envia o erro ao backend (POST /clienteErro) para ficar
    // rastreável nos logs do servidor (stderr -> Cloud Run/GCloud). Qualquer
    // falha aqui é silenciada para não afetar o fallback exibido ao usuário.
    reportar(error, errorInfo) {
        try {
            let participante = null;
            try {
                participante = window.sessionStorage.getItem('participantIdUsuario');
            } catch (e) { /* sessionStorage indisponível */ }

            const payload = {
                mensagem: error && error.message ? String(error.message) : 'erro de renderizacao no front',
                stack: error && error.stack ? String(error.stack) : null,
                componentStack: errorInfo && errorInfo.componentStack ? String(errorInfo.componentStack) : null,
                url: typeof window !== 'undefined' ? window.location.href : null,
                rota: this.props && this.props.pathname
                    ? this.props.pathname
                    : (typeof window !== 'undefined' ? window.location.pathname : null),
                participante: participante,
                data: (this.state.ocorridoEm instanceof Date ? this.state.ocorridoEm : new Date()).toISOString(),
            };

            const apiUrl = process.env.REACT_APP_API_URL || '';
            if (typeof fetch === 'function') {
                fetch(apiUrl + '/clienteErro', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify(payload),
                    keepalive: true,
                }).catch(function () { /* best-effort */ });
            }
        } catch (e) {
            // best-effort: nunca propaga
        }
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        // Traduz via getText (idioma atual: pt OU en), com fallback hardcoded —
        // o boundary é a última linha de defesa, então nunca pode quebrar aqui.
        // Idioma traduzido só para CONVIDADO puro: tem sessão de participante de
        // evento (sessionStorage) E NÃO tem sessão interna de operador. Operador
        // logado — mesmo que também tenha logado como participante — e anônimo
        // veem o padrão em português (o `padrao` já é o texto em pt).
        let ehConvidado = false;
        try {
            ehConvidado = !getInternalAuth().token;
        } catch (e) {
            ehConvidado = false;
        }

        const t = (key, padrao) => {
            if (!ehConvidado) {
                return padrao;
            }
            try {
                const valor = getText(key);
                return (valor === undefined || valor === null || valor === '') ? padrao : valor;
            } catch (e) {
                return padrao;
            }
        };

        // data formatada no mesmo idioma do texto (convidado -> idioma dele;
        // operador/anônimo -> pt-BR)
        let locale = 'pt-BR';
        try {
            if (ehConvidado) locale = getIdiomaLocale();
        } catch (e) {
            locale = 'pt-BR';
        }
        const ocorridoEm = this.state.ocorridoEm instanceof Date ? this.state.ocorridoEm : new Date();

        return (
            <div
                style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    background: '#f4f5f7',
                }}
            >
                <div
                    style={{
                        maxWidth: 440,
                        width: '100%',
                        background: '#fff',
                        borderRadius: 12,
                        boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
                        padding: '32px',
                        textAlign: 'center',
                    }}
                >
                    <img
                        src={logo}
                        alt=""
                        style={{ maxHeight: 44, width: 'auto', objectFit: 'contain', marginBottom: 20 }}
                    />
                    <h4 style={{ margin: '0 0 8px', color: '#222' }}>
                        {t('errorBoundaryTitle', 'Algo deu errado ao carregar esta página')}
                    </h4>
                    <p style={{ margin: '0 0 24px', color: '#666' }}>
                        {t('errorBoundaryMessage', 'Tente recarregar. Se o problema continuar, entre em contato com a organização do evento.')}
                    </p>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={{
                            background: '#0d6efd',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            padding: '12px 24px',
                            fontSize: 15,
                            cursor: 'pointer',
                        }}
                    >
                        {t('errorBoundaryReload', 'Recarregar a página')}
                    </button>
                    <p style={{ margin: '20px 0 0', color: '#c9c9c9', fontSize: 12 }}>
                        {ocorridoEm.toLocaleString(locale)}
                    </p>
                </div>
            </div>
        );
    }
}
